/**
 * MKS-332 — the money vocabulary, once.
 *
 * Before this, "revenue" meant four different things across the app and margin
 * had five formulas. The fix is not better tooltips on five definitions; it is
 * one definition with a tooltip. Every money figure in the SPA takes its label
 * AND its explanation from this file, so a surface cannot quietly introduce a
 * sixth meaning: to show a number you must name it with one of these keys.
 *
 * `definition` is repeated verbatim from `MONEY_VOCABULARY` in
 * `backend/src/services/revenueService.ts` — the sentence a user reads in a
 * tooltip and the sentence a developer reads above the computation are the
 * same sentence, deliberately.
 */

export type MoneyMetricKey = 'earned' | 'invoiced' | 'paid' | 'cost' | 'margin';

export interface MoneyMetric {
  /** The EN label shown on every surface. Never translated per-surface. */
  label: string;
  /** The Czech word the team actually says, shown in the glossary. */
  czech: string;
  /** One sentence. Shown in the info tooltip next to the figure. */
  definition: string;
  /**
   * The things people get wrong about this figure, shown only in the glossary
   * page — a tooltip that needs three paragraphs is a tooltip nobody reads.
   */
  notes: string[];
}

export const MONEY_GLOSSARY: Record<MoneyMetricKey, MoneyMetric> = {
  earned: {
    label: 'Earned',
    czech: 'vyděláno',
    definition:
      'What the period earned: approved hours × rate by entry date for T&M, the monthly paušál (plus performed overage) for retainers, and the attributed invoiced share for fixed price. Gross — an invoice discount is an invoicing decision and does not reduce what was earned.',
    notes: [
      'Gross of discount, on purpose. Giving a client 10 % off does not mean the team earned 10 % less that month; the discount shows up in Invoiced.',
      'A retainer earns its paušál in every month it was active, whether or not the hours were drawn. Unused allowance is not negative revenue.',
      'A month with no approved entry and no invoice earns nothing — an all-time view cannot invent revenue for a dormant month.',
    ],
  },
  invoiced: {
    label: 'Invoiced',
    czech: 'vyfakturováno',
    definition:
      "What was billed for the period: the invoice's amount after discount, attributed to the period by the invoice's service period and split per project by the invoice's own per-project breakdown.",
    notes: [
      'Attributed by SERVICE period, not issue date. An invoice issued in April for March work is March’s Invoiced.',
      'A multi-project invoice is split by its own per-project breakdown — a project is never shown the full amount of an invoice it only partly caused.',
      'Net of discount, unlike Earned. Earned minus Invoiced for the same period is, roughly, what was discounted or not yet billed.',
    ],
  },
  paid: {
    label: 'Paid',
    czech: 'zaplaceno',
    definition:
      'Invoiced amounts whose payment was recorded in the period, split per project the same way Invoiced is.',
    notes: [
      'Counted in the month the payment was recorded, not the month the work was done. Paid and Invoiced for the same period are not expected to agree.',
      'An invoice nobody has marked paid counts as unpaid, however old it is — this is manual payment tracking, not an accounting integration.',
    ],
  },
  cost: {
    label: 'Cost',
    czech: 'náklady',
    definition:
      'What the period cost: the labour cost of the approved hours plus approved expense receipts dated in the period.',
    notes: [
      'Labour cost is the per-entry cost the app already stamps — the person’s or assignment’s cost rate plus overhead.',
      'Approved expenses are always included, at every level, so Cost means one thing everywhere.',
      'Salaries, freelancer invoices and anything from accounting are NOT in here yet. That is a separate ticket.',
      'Visible to admin and finance only.',
    ],
  },
  margin: {
    label: 'Margin',
    czech: 'marže',
    definition: 'Earned minus Cost, as a share of Earned. Undefined when nothing was earned.',
    notes: [
      'One formula everywhere: (Earned − Cost) ÷ Earned. Not profit ÷ cost, and not margin ÷ invoiced — those answer different questions and are why the same project used to read three different margins on three pages.',
      'A period that earned nothing shows “—”, not 0 %.',
      'Totals margin is computed from the totals, never averaged across projects.',
      'Visible to admin and finance only.',
    ],
  },
};

/** The order the glossary page and any side-by-side KPI band use. */
export const MONEY_METRIC_ORDER: MoneyMetricKey[] = [
  'earned',
  'invoiced',
  'paid',
  'cost',
  'margin',
];

/** Shorthand for `MONEY_GLOSSARY[key].definition` at a tooltip call site. */
export function moneyDefinition(key: MoneyMetricKey): string {
  return MONEY_GLOSSARY[key].definition;
}

/** Shorthand for `MONEY_GLOSSARY[key].label`. */
export function moneyLabel(key: MoneyMetricKey): string {
  return MONEY_GLOSSARY[key].label;
}

/**
 * The one margin formula — a mirror of `computeMarginPercent` in
 * `backend/src/services/revenueService.ts`, kept so a surface that already
 * holds Earned and Cost can render Margin without a round trip, and so it
 * renders the SAME number the backend would.
 *
 * `(earned − cost) ÷ earned × 100`, `null` when nothing was earned. Needed in
 * the SPA because several surfaces still carry a pre-MKS-332 percentage field
 * computed as profit ÷ cost — a different question with a different answer —
 * and this is what replaces it at the render site.
 *
 * `cost` is optional so a redacted payload (no cost ⇒ no margin to show)
 * produces `null` rather than a margin of 100 %.
 */
export function computeMarginPercent(earned: number, cost: number | undefined): number | null {
  if (cost === undefined) return null;
  if (!(earned > 0)) return null;
  return ((earned - cost) / earned) * 100;
}

/** Renders a margin percentage, or an em dash when it is undefined. */
export function formatMarginPercent(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(1)}%`;
}
